import { useState, useEffect, useCallback } from "react";
import { Banknote, Plus, Trash2, ChevronUp, ChevronDown, Eye, Check, Loader2, Copy } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorView } from "@/components/StateViews";
import DealFormFields from "@/components/DealFormFields";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";

const FIELD_TYPES = ["text", "longtext", "number", "currency", "date", "dropdown", "radio", "checkbox", "url", "section"];
const HAS_OPTIONS = ["dropdown", "radio", "checkbox"];
const STATUS_STYLE = { active: "bg-emerald-100 text-emerald-700", draft: "bg-amber-100 text-amber-700", archived: "bg-slate-100 text-slate-500" };

const slug = (s) => (s || "field").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || `field_${Date.now()}`;

export default function DealForms() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);   // draft being edited {id?, name, fields[]}
  const [preview, setPreview] = useState(false);
  const [previewVals, setPreviewVals] = useState({});
  const [activateId, setActivateId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/deal-forms");
      setForms(data.data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = forms.find((f) => f.status === "active");

  const newDraftFromActive = () => {
    const base = active || forms[0];
    setEditing({
      name: `${base?.name || "Deal Details"} (new draft)`,
      fields: JSON.parse(JSON.stringify(base?.fields || [])),
      summary_amount_field: base?.summary_amount_field || "total_deal_amount",
      summary_services_field: base?.summary_services_field || "services_proposed",
      summary_probability_field: base?.summary_probability_field || "commercial_probability",
    });
    setPreview(false);
  };

  const editDraft = (f) => { setEditing({ ...f, fields: JSON.parse(JSON.stringify(f.fields)) }); setPreview(false); };

  const updField = (i, patch) => setEditing((e) => {
    const fields = [...e.fields]; fields[i] = { ...fields[i], ...patch }; return { ...e, fields };
  });
  const move = (i, dir) => setEditing((e) => {
    const fields = [...e.fields]; const j = i + dir;
    if (j < 0 || j >= fields.length) return e;
    [fields[i], fields[j]] = [fields[j], fields[i]]; return { ...e, fields };
  });
  const addField = () => setEditing((e) => ({ ...e, fields: [...e.fields, { field_id: `field_${Date.now()}`, label: "New field", field_type: "text", required: false, options: [] }] }));
  const delField = (i) => setEditing((e) => ({ ...e, fields: e.fields.filter((_, k) => k !== i) }));

  const saveDraft = async () => {
    if (!editing.name?.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = {
        name: editing.name.trim(),
        fields: editing.fields.map((f) => ({ ...f, field_id: f.field_id || slug(f.label) })),
        summary_amount_field: editing.summary_amount_field,
        summary_services_field: editing.summary_services_field,
        summary_probability_field: editing.summary_probability_field,
      };
      if (editing.id) await api.put(`/deal-forms/${editing.id}`, payload);
      else await api.post("/deal-forms", payload);
      toast.success("Draft saved");
      setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doActivate = async () => {
    setBusy(true);
    try { await api.post(`/deal-forms/${activateId}/activate`); toast.success("Version activated"); setActivateId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/deal-forms/${id}`); toast.success("Draft deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  if (loading) return <Loading label="Loading deal forms..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  return (
    <div className="space-y-6" data-testid="deal-forms-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Banknote className="h-7 w-7 text-emerald-600" />Deal Forms</h1>
          <p className="text-sm text-ink/60">Versioned Deal Details templates. New versions apply only to future submissions — old leads keep their saved version.</p>
        </div>
        {!editing && <button onClick={newDraftFromActive} data-testid="deal-new-draft" className="flex h-10 items-center gap-1.5 rounded-lg bg-loop px-4 text-sm font-semibold text-white hover:bg-loophover"><Copy className="h-4 w-4" />New draft from active</button>}
      </div>

      {!editing ? (
        <div className="grid gap-3 md:grid-cols-2">
          {forms.map((f) => (
            <div key={f.id} data-testid={`deal-form-${f.version}`} className="rounded-2xl border border-sky/70 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-sora text-lg font-bold text-navy">v{f.version}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLE[f.status]}`}>{f.status}</span>
                </div>
                <span className="text-xs text-ink/45">{f.fields.length} fields</span>
              </div>
              <p className="mt-1 text-sm font-medium text-ink/80">{f.name}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {f.status === "draft" && <button onClick={() => editDraft(f)} data-testid={`deal-edit-${f.version}`} className="rounded-lg border border-sky px-2.5 py-1 text-xs font-medium text-navy hover:bg-mist">Edit</button>}
                {f.status === "draft" && <button onClick={() => setActivateId(f.id)} data-testid={`deal-activate-${f.version}`} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700"><Check className="h-3 w-3" />Activate</button>}
                {f.status === "draft" && <button onClick={() => del(f.id)} className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-medium text-red-500 hover:bg-red-50">Delete</button>}
                {f.status === "archived" && <button onClick={() => setActivateId(f.id)} className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1 text-xs font-medium text-navy hover:bg-mist"><Check className="h-3 w-3" />Re-activate</button>}
                {f.status === "active" && <span className="flex items-center gap-1 text-xs text-emerald-600"><Check className="h-3 w-3" />In use for new deals</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4 rounded-2xl border border-sky/70 bg-white p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <input value={editing.name} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} data-testid="deal-draft-name" className="h-10 min-w-[260px] flex-1 rounded-lg border border-sky px-3 text-sm font-medium outline-none focus:border-loop" />
            <div className="flex gap-2">
              <button onClick={() => setPreview((p) => !p)} data-testid="deal-preview-toggle" className="flex items-center gap-1 rounded-lg border border-sky px-3 py-2 text-sm font-medium text-navy hover:bg-mist"><Eye className="h-4 w-4" />{preview ? "Edit" : "Preview"}</button>
              <button onClick={() => setEditing(null)} className="rounded-lg border border-sky px-3 py-2 text-sm font-medium text-ink/60 hover:bg-mist">Cancel</button>
              <button onClick={saveDraft} disabled={busy} data-testid="deal-save-draft" className="flex items-center gap-1.5 rounded-lg bg-loop px-4 py-2 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save draft</button>
            </div>
          </div>

          {preview ? (
            <div className="rounded-xl border border-sky/70 bg-mist/40 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-ink/40">Preview — how salespeople will see it</p>
              <DealFormFields fields={editing.fields} values={previewVals} onChange={(id, v) => setPreviewVals((s) => ({ ...s, [id]: v }))} idPrefix="preview" />
            </div>
          ) : (
            <div className="space-y-2">
              {editing.fields.map((f, i) => (
                <div key={i} data-testid={`deal-field-row-${i}`} className="rounded-xl border border-sky/70 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <input value={f.label} onChange={(e) => updField(i, { label: e.target.value })} placeholder="Question label" data-testid={`deal-field-label-${i}`} className="h-9 min-w-[200px] flex-1 rounded-lg border border-sky px-2.5 text-sm outline-none focus:border-loop" />
                    <select value={f.field_type} onChange={(e) => updField(i, { field_type: e.target.value })} data-testid={`deal-field-type-${i}`} className="h-9 rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop">
                      {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {f.field_type !== "section" && (
                      <label className="flex items-center gap-1 text-xs text-ink/60"><input type="checkbox" checked={!!f.required} onChange={(e) => updField(i, { required: e.target.checked })} />Required</label>
                    )}
                    <button onClick={() => move(i, -1)} className="rounded p-1 text-ink/40 hover:bg-mist"><ChevronUp className="h-4 w-4" /></button>
                    <button onClick={() => move(i, 1)} className="rounded p-1 text-ink/40 hover:bg-mist"><ChevronDown className="h-4 w-4" /></button>
                    <button onClick={() => delField(i)} className="rounded p-1 text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  {HAS_OPTIONS.includes(f.field_type) && (
                    <input
                      value={(f.options || []).map((o) => o.value).join(", ")}
                      onChange={(e) => updField(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean).map((v) => ({ value: v })) })}
                      placeholder="Options, comma-separated"
                      className="mt-2 h-9 w-full rounded-lg border border-sky px-2.5 text-sm outline-none focus:border-loop"
                    />
                  )}
                </div>
              ))}
              <button onClick={addField} data-testid="deal-add-field" className="flex items-center gap-1.5 rounded-lg border border-dashed border-sky px-3 py-2 text-sm font-medium text-loop hover:bg-sky/20"><Plus className="h-4 w-4" />Add field</button>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!activateId}
        onOpenChange={(v) => !v && setActivateId(null)}
        title="Activate this deal form version?"
        description="This will apply only to new submissions. Existing leads will keep their old questions and answers. The current active version will be archived (read-only)."
        confirmLabel="Activate version"
        testid="deal-activate-confirm"
        onConfirm={doActivate}
        destructive={false}
      />
    </div>
  );
}
